// Copyright (c) 2026, Dokos SAS and Contributors
// License: GNU General Public License v3. See license.txt
// @dokos: DOM helpers shared by the Project Management view modes and dialogs.
// Everything is built with DOM APIs (textContent / attributes): user data is never
// injected as HTML.

import { hashColor } from "./PMViewEventSource";

/**
 * @dokos: create an element.
 * @param {string} tag
 * @param {Object} [props] `class`, `text`, `title`, `style` (object), `attrs` (object),
 *   `on` (object of listeners); other keys are set as properties.
 * @param {...(Node|string|null|undefined|false)} children
 * @returns {HTMLElement}
 */
export function h(tag, props = {}, ...children) {
	const node = document.createElement(tag);
	for (const [key, value] of Object.entries(props || {})) {
		if (value == null || value === false) continue;
		if (key === "class") node.className = Array.isArray(value) ? value.filter(Boolean).join(" ") : value;
		else if (key === "text") node.textContent = value;
		else if (key === "style") {
			// custom properties (--x) need setProperty, Object.assign ignores them
			for (const [prop, v] of Object.entries(value)) {
				if (v == null) continue;
				if (prop.startsWith("--")) node.style.setProperty(prop, v);
				else node.style[prop] = v;
			}
		} else if (key === "attrs") {
			for (const [name, v] of Object.entries(value)) {
				if (v != null && v !== false) node.setAttribute(name, v === true ? "" : String(v));
			}
		} else if (key === "on") {
			for (const [event, listener] of Object.entries(value)) node.addEventListener(event, listener);
		} else node[key] = value;
	}
	for (const child of children.flat()) {
		if (child == null || child === false) continue;
		node.append(child instanceof Node ? child : document.createTextNode(String(child)));
	}
	return node;
}

/**
 * @dokos: server messages echo the typed text inside <b>/<strong>: escape everything but
 * that tag.
 */
export function safeBoldHtml(message) {
	return frappe.utils.escape_html(String(message ?? "")).replace(/&lt;(\/?)(b|strong)&gt;/g, "<$1$2>");
}

/** @dokos: an icon of the desk sprite (trusted markup from frappe.utils.icon). */
export function icon(name, size = "sm") {
	const span = document.createElement("span");
	span.className = "pm-icon";
	span.setAttribute("aria-hidden", "true");
	span.innerHTML = frappe.utils.icon(name, size);
	return span;
}

/**
 * @dokos: "6h", "1h30", "45m" from a number of minutes.
 * @param {number} minutes
 */
export function formatMinutes(minutes) {
	minutes = Math.round(Number(minutes) || 0);
	const sign = minutes < 0 ? "-" : "";
	minutes = Math.abs(minutes);
	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;
	if (!hours) return `${sign}${rest}m`;
	return rest ? `${sign}${hours}h${String(rest).padStart(2, "0")}` : `${sign}${hours}h`;
}

/**
 * @dokos: `formatMinutes` for a narrow cell: from 10 hours on, rounded to the hour
 * ("44h54" → "45h"); the exact value goes in the tooltip.
 * @param {number} minutes
 */
export function formatMinutesShort(minutes) {
	minutes = Math.round(Number(minutes) || 0);
	if (Math.abs(minutes) < 600) return formatMinutes(minutes);
	return formatMinutes(Math.sign(minutes) * Math.round(Math.abs(minutes) / 60) * 60);
}

/**
 * @dokos: load level of `load` against `capacity` (minutes): "over" above 100 %, "high"
 * above 90 %, else "ok". A load with no capacity is over.
 */
export function loadLevel(load, capacity) {
	const ratio = capacity ? load / capacity : load ? 2 : 0;
	return { ratio, level: ratio > 1 ? "over" : ratio > 0.9 ? "high" : "ok" };
}

/**
 * @dokos: what is left of `capacity` after `load` (minutes): "Remaining 3h" or "Over by 2h59".
 * @param {number} load
 * @param {number} capacity
 * @param {(minutes: number) => string} [format]
 * @returns {{remaining: number, level: string, text: string}}
 */
export function remainingText(load, capacity, format = formatMinutes) {
	const remaining = (capacity || 0) - (load || 0);
	const { level } = loadLevel(load, capacity);
	const text =
		remaining < 0
			? __("Over by {0}", [format(-remaining)], "PM View")
			: __("Remaining {0}", [format(remaining)], "PM View");
	return { remaining, level, text };
}

/** @dokos: "12h" / "7.5h" from a number of hours. */
export function formatHours(hours) {
	const value = Math.round((Number(hours) || 0) * 100) / 100;
	return `${value}h`;
}

/**
 * @dokos: color of an activity type: its own `color`, else a stable hash color of the
 * activity type name, else of `fallbackKey` (e.g. the project), else grey.
 * @param {string?} activityType
 * @param {string?} color
 * @param {string?} [fallbackKey]
 */
export function activityColor(activityType, color, fallbackKey) {
	const hashKey = activityType || fallbackKey;
	if (color && frappe.ui.color.validate_hex(color)) return color;
	const shade = color && frappe.ui.color.get_color_shade(color, "default");
	if (shade) return shade;
	return hashKey ? hashColor(hashKey) : "var(--gray-500)";
}

let activityTypesPromise = null;
/**
 * @dokos: `{name: color}` of the enabled activity types (cached for the page life).
 * @returns {Promise<Record<string, string>>}
 */
export function getActivityTypeColors() {
	if (!activityTypesPromise) {
		activityTypesPromise = frappe.db
			.get_list("Activity Type", {
				fields: ["name", "color"],
				filters: { disabled: 0 },
				limit: 500,
			})
			.then((rows) => Object.fromEntries((rows || []).map((r) => [r.name, r.color])))
			.catch(() => {
				activityTypesPromise = null;
				return {};
			});
	}
	return activityTypesPromise;
}

/** @dokos: forget the cached activity type colors (after an Activity Type change). */
export function clearActivityTypeColors() {
	activityTypesPromise = null;
}

/**
 * @dokos: work chip "6h · Design" (hours on the first line, activity type below).
 * @param {Object} opts
 * @param {number} opts.minutes
 * @param {string?} [opts.activityType]
 * @param {string} [opts.suffix] appended to the hours (e.g. "/d")
 * @param {boolean} [opts.over] over capacity: a warning mark, not only a color
 * @returns {HTMLElement}
 */
export function renderChip({ minutes, activityType, suffix = "", over = false }) {
	return h(
		"div",
		{ class: "pm-chip-body" },
		h("span", { class: "pm-chip-hours" }, over ? overMark() : null, formatMinutes(minutes) + suffix),
		activityType ? h("span", { class: "pm-chip-type ellipsis", text: __(activityType) }) : null
	);
}

const OVER_ICON =
	'<svg viewBox="0 0 16 16" width="11" height="11" fill="currentColor" aria-hidden="true" focusable="false"><path d="M8 1.5 15 14.5H1z"/></svg>';

/**
 * @dokos: "over capacity" mark: a triangle (not only a color) and, for screen readers,
 * the words.
 * @param {boolean} [withText] add the visually hidden text (off in the legend, which has a label)
 */
export function overMark(withText = true) {
	const mark = h("span", { class: "pm-over-mark" });
	mark.innerHTML = OVER_ICON; // static markup
	if (withText) mark.append(h("span", { class: "sr-only", text: __("Over capacity", null, "PM View") }));
	return mark;
}

/** @dokos: small colored square used in legends and activity type chips. */
export function swatch(color) {
	return h("span", {
		class: "pm-swatch",
		attrs: { "aria-hidden": "true" },
		style: { "--pm-color": color },
	});
}

/**
 * @dokos: avatar of an employee: initials on a palette color, covered by the employee
 * (or linked user) image once it has loaded. A broken image (404/500) is dropped so the
 * initials stay visible instead of an empty grey circle.
 * @param {{employee_name?: string, image?: string, employee?: string, user_id?: string}} person
 * @param {"xs"|"sm"|"md"} [size]
 */
export function avatar(person, size = "sm") {
	const name = person?.employee_name || person?.employee || "?";
	const node = h("span", {
		class: ["avatar", `pm-avatar pm-avatar--${size}`],
		title: name,
	});
	const [bg, fg] = frappe.get_palette(name) || [];
	const frame = h("span", {
		class: "avatar-frame standard-image",
		text: frappe.get_abbr(name, 2),
		style: {
			backgroundColor: bg ? `var(${bg})` : null,
			color: fg ? `var(${fg})` : null,
		},
	});
	node.append(frame);

	const image = person?.image || (person?.user_id && frappe.user_info(person.user_id)?.image);
	if (image) {
		const img = h("img", {
			class: "pm-avatar-img",
			alt: "",
			attrs: { "aria-hidden": "true" },
			on: { error: () => img.remove() },
		});
		img.src = image;
		node.append(img);
	}
	return node;
}

/**
 * @dokos: overlapping avatars; the overflow is shown as "+N".
 * @param {Array} people
 * @param {number} [limit]
 */
export function avatarStack(people, limit = 3) {
	const stack = h("span", { class: "pm-avatar-stack" });
	(people || []).slice(0, limit).forEach((p) => stack.append(avatar(p, "xs")));
	const extra = (people || []).length - limit;
	if (extra > 0) {
		stack.append(
			h(
				"span",
				{
					class: "avatar pm-avatar pm-avatar--xs",
					title: people
						.slice(limit)
						.map((p) => p.employee_name || p.employee)
						.join(", "),
				},
				h("span", { class: "avatar-frame standard-image", text: `+${extra}` })
			)
		);
	}
	return stack;
}

/**
 * @dokos: translated status NOUN of a Project / Task. "Open" alone is translated as the verb
 * by some apps' catalogues (fr: "Ouvrir", the merged catalogue's last word), so it carries the
 * doctype as context — the context the desk Select formatter uses (`__(value, null, df.parent)`),
 * which also gives the gender ("Ouvert" project, "Ouverte" task).
 * @param {"Project"|"Task"} doctype
 * @param {string} status English doc value
 */
export function statusLabel(doctype, status) {
	if (status === "Open") {
		return doctype === "Task" ? __("Open", null, "Task") : __("Open", null, "Project");
	}
	return __(status);
}

/** @dokos: status pill of a Project (es-badge, colour from the shared status map). */
export function projectStatusPill(status) {
	if (!status) return null;
	return erpnext.dokos.ui.pill({ label: statusLabel("Project", status), status })[0];
}

// @dokos: dot look per Task status; the colours follow the status map (pm-status-dot--*)
const TASK_STATUS_CLASS = {
	Open: "open",
	Working: "working",
	"Pending Review": "review",
	Overdue: "overdue",
	Completed: "done",
	Cancelled: "cancelled",
	Template: "template",
};
/** @dokos: status dot of a Task, next to its status in words (`taskStatusLabel`). */
export function taskStatusDot(status) {
	return h("span", {
		class: ["pm-status-dot", `pm-status-dot--${TASK_STATUS_CLASS[status] || "open"}`],
		attrs: { "aria-hidden": "true" },
	});
}

/** @dokos: translated status of a Task (a noun: "Open" goes through its context). */
export function taskStatusLabel(status) {
	return statusLabel("Task", status || "Open");
}

// -----------------------------------------------------------------------------------
// @dokos: localized dates. moment's locale data is not loaded in the desk (its "fr"
// locale formats in English), so labels go through Intl in the user language.
// -----------------------------------------------------------------------------------

const dateFormatters = new Map();
/** @dokos: cached Intl.DateTimeFormat in the user language (falls back to the browser's). */
function dateFormatter(options) {
	const key = JSON.stringify(options);
	if (!dateFormatters.has(key)) {
		let formatter;
		try {
			formatter = new Intl.DateTimeFormat(frappe.boot?.lang || undefined, options);
		} catch {
			formatter = new Intl.DateTimeFormat(undefined, options);
		}
		dateFormatters.set(key, formatter);
	}
	return dateFormatters.get(key);
}

/** @dokos: local Date of an ISO day "YYYY-MM-DD" (or any moment-able value). */
function toDate(day) {
	return moment(day, "YYYY-MM-DD").toDate();
}

/** @dokos: named date formats of the view (Intl options). */
const DATE_FORMATS = {
	weekday: { weekday: "short" }, // "mar." / "Tue"
	weekdayDay: { weekday: "short", day: "numeric" }, // "mar. 22" / "22 Tue"
	weekdayDayMonth: { weekday: "short", day: "numeric", month: "short" }, // "mar. 22 sept."
	longDay: { weekday: "long", day: "numeric", month: "long" }, // "mardi 22 septembre"
	dayMonth: { day: "numeric", month: "short" }, // "22 sept." / "Sep 22"
	fullDay: { weekday: "long", day: "numeric", month: "long", year: "numeric" },
};

/**
 * @dokos: a day formatted in the user language.
 * @param {string|Date} day ISO day or Date
 * @param {keyof DATE_FORMATS} [format]
 */
export function formatDay(day, format = "weekdayDay") {
	return dateFormatter(DATE_FORMATS[format] || DATE_FORMATS.weekdayDay).format(toDate(day));
}

/**
 * @dokos: "21–27 sept. 2026" / "28 sept. – 4 oct. 2026" in the user language.
 * @param {string} start ISO day
 * @param {string} end ISO day
 */
export function formatDayRange(start, end) {
	const formatter = dateFormatter({ day: "numeric", month: "short", year: "numeric" });
	if (typeof formatter.formatRange === "function") {
		return formatter.formatRange(toDate(start), toDate(end));
	}
	return `${formatter.format(toDate(start))} – ${formatter.format(toDate(end))}`;
}

/**
 * @dokos: horizontally scroll a strip of buttons (mobile day chips) so `item` is centered,
 * without scrolling the page vertically as `scrollIntoView` would.
 * @param {HTMLElement} strip scrollable container (`position: relative`)
 * @param {HTMLElement?} item
 */
export function scrollIntoStrip(strip, item) {
	if (!strip || !item) return;
	strip.scrollLeft = Math.max(0, item.offsetLeft - (strip.clientWidth - item.offsetWidth) / 2);
}

/** @dokos: YYYY-MM-DD of a Date / moment-able value. */
export function ymd(date) {
	return moment(date).format("YYYY-MM-DD");
}

/**
 * @dokos: every ISO day of the inclusive range [start, end].
 * @returns {string[]}
 */
export function daysBetween(start, end) {
	const out = [];
	if (!start || !end) return out;
	const cursor = moment(start, "YYYY-MM-DD");
	const last = moment(end, "YYYY-MM-DD");
	for (let i = 0; cursor.isSameOrBefore(last, "day") && i < 400; i++) {
		out.push(cursor.format("YYYY-MM-DD"));
		cursor.add(1, "day");
	}
	return out;
}

/**
 * @dokos: a button opening a popover with an Employee Link control. The resource area
 * has several `overflow: hidden` parents, so the control lives in a popover mounted in
 * `container` rather than inline.
 * @param {Object} opts
 * @param {string} opts.label button text
 * @param {HTMLElement} opts.container popover container (e.g. the page parent)
 * @param {() => string[]} [opts.exclude] employees not offered
 * @param {(employee: string) => void} opts.onPick
 * @returns {HTMLElement}
 */
export function employeePicker({ label, container, exclude = () => [], onPick }) {
	// @dokos: an es-button (façade); the popover is wired below, not through onclick
	const button = erpnext.dokos.ui.button({
		label,
		icon: "plus",
		size: "sm",
		css_class: "pm-employee-picker",
	})[0];
	const wrapper = h("div", { class: "pm-employee-picker-popover" });
	const control = frappe.ui.form.make_control({
		parent: wrapper,
		df: {
			fieldtype: "Link",
			fieldname: "pm_pick_employee",
			options: "Employee",
			placeholder: __("Employee"),
			get_query: () => {
				const filters = { status: "Active" };
				const excluded = exclude();
				if (excluded.length) filters.name = ["not in", excluded];
				return { filters };
			},
			change: () => {
				const employee = control.get_value();
				if (!employee) return;
				control.set_value("");
				hide();
				onPick(employee);
			},
		},
		render_input: true,
		only_input: true,
	});

	$(button).popover({
		html: true,
		animation: false,
		content: wrapper,
		placement: "right",
		trigger: "manual",
		container,
	});
	const isShown = () => {
		const tip = $(button).data("bs.popover")?.tip;
		return tip?.classList.contains("show") && tip?.isConnected;
	};
	const hide = () => {
		if (isShown()) {
			$(button).popover("hide");
			button.focus();
		}
	};
	button.addEventListener("click", () => {
		if (isShown()) return hide();
		$(button).popover("show");
		control.$input.focus();
	});
	control.$input.on("awesomplete-close", hide);
	control.$input.on("keydown", (e) => e.key === "Escape" && hide());
	return button;
}

/**
 * @dokos: the task tree of a plan. Siblings keep the payload order; a task whose parent is
 * not in the plan is a root.
 * @param {Array} tasks tasks of one project (`name`, `parent_task`)
 * @returns {{roots: Array, children: Object<string, Array>, parent: Object<string, string>}}
 */
export function taskTree(tasks) {
	const names = new Set(tasks.map((t) => t.name));
	const roots = [];
	const children = {};
	const parent = {};
	for (const task of tasks) {
		if (task.parent_task && task.parent_task !== task.name && names.has(task.parent_task)) {
			(children[task.parent_task] = children[task.parent_task] || []).push(task);
			parent[task.name] = task.parent_task;
		} else {
			roots.push(task);
		}
	}
	return { roots, children, parent };
}

/**
 * @dokos: consecutive projects of the same Project Type (the server sorts them by type,
 * untyped last).
 * @returns {Array<{type: string|null, label: string, projects: Array}>}
 */
export function groupProjectsByType(projects) {
	const groups = [];
	for (const project of projects) {
		const type = project.project_type || null;
		let group = groups[groups.length - 1];
		if (!group || group.type !== type) {
			group = {
				type,
				label: type ? __(type) : __("No project type", null, "PM View"),
				projects: [],
			};
			groups.push(group);
		}
		group.projects.push(project);
	}
	return groups;
}
